import {
    ClassificationTypes, Directionality, UnitType,
} from 'BMapsSrc/integrations/properties_common';
import { PropApplicability } from 'BMapsSrc/model/ScopedPropertyGroup';
import { App } from 'BMapsSrc/BMapsApp';
import { deepFreeze } from 'BMapsUtil/js_utils';
import { JobDefinition, JobResultDestinations, JobTypes } from '../JobDefinition';

const scopedPropertyMetadata = deepFreeze({
    compound: {
        childGroups: {
            maplight: {
                metadata: {
                    label: 'MapLight ADMET properties',
                    tooltipHeading: 'MapLight ADMET prediction',
                },
                properties: {
                    ADMET_ames: {
                        metadata: {
                            label: 'Ames Mutagenicity',
                            description: 'Predicts whether a compound is likely to be mutagenic (DNA-damaging) in the Ames bacterial test.',
                            classificationType: ClassificationTypes.BINARY,
                            unit: 'Probability',
                            directionality: Directionality.MINIMIZE,
                            range: '0.0 - 1.0',
                            goodRange: '<0.5',
                            r2: 0.865,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    ADMET_bbb_martins: {
                        metadata: {
                            label: 'Blood-Brain Barrier Permeability',
                            description: 'Predicts whether a compound can cross the blood-brain barrier and potentially act on the central nervous system.',
                            classificationType: ClassificationTypes.BINARY,
                            unit: 'Probability',
                            directionality: Directionality.MAXIMIZE,
                            range: '0.0 - 1.0',
                            goodRange: '>0.5',
                            r2: 0.92,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    ADMET_bioavailability_ma: {
                        metadata: {
                            label: 'Oral Bioavailability (F%)',
                            description: 'Estimates the fraction of an orally administered drug that reaches systemic circulation.',
                            classificationType: ClassificationTypes.BINARY,
                            unit: 'Probability',
                            directionality: Directionality.MAXIMIZE,
                            range: '0.0 - 1.0',
                            goodRange: '>0.5',
                            r2: 0.741,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    ADMET_caco2_wang: {
                        metadata: {
                            label: 'Caco-2 Permeability (Papp)',
                            description: 'Predicts permeability across intestinal epithelial cells (Caco-2 assay proxy for oral absorption).',
                            classificationType: ClassificationTypes.REGRESSION,
                            unit: '10^-6 cm/s',
                            directionality: Directionality.MAXIMIZE,
                            range: '0.1 - 100',
                            goodRange: '>10',
                            r2: 0.276,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    ADMET_clearance_hepatocyte_az: {
                        metadata: {
                            label: 'Hepatocyte Clearance',
                            description: 'Predicts hepatic clearance rates using hepatocyte data as a proxy for drug metabolism.',
                            classificationType: ClassificationTypes.REGRESSION,
                            unit: 'mL/min/kg',
                            directionality: Directionality.NOT_APPLICABLE,
                            range: '<13 (low), 13 - 31 (mod), >31 (high)',
                            goodRange: '13 - 31',
                            r2: 0.469,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    ADMET_clearance_microsome_az: {
                        metadata: {
                            label: 'Microsome Clearance',
                            description: 'Predicts metabolic clearance based on liver microsomes containing CYP enzymes.',
                            classificationType: ClassificationTypes.REGRESSION,
                            unit: 'mL/min/kg',
                            directionality: Directionality.NOT_APPLICABLE,
                            range: '<13 (low), 13 - 31 (mod), >31 (high)',
                            goodRange: '13 - 31',
                            r2: 0.625,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    ADMET_cyp2c9_substrate_carbonmangels: {
                        metadata: {
                            label: 'CYP2C9 Substrate',
                            description: 'Predicts whether the compound is metabolized by CYP2C9 enzyme.',
                            classificationType: ClassificationTypes.BINARY,
                            unit: 'Probability',
                            directionality: Directionality.MINIMIZE,
                            range: '0.0 - 1.0',
                            goodRange: '<0.5',
                            r2: 0.411,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    ADMET_cyp2c9_veith: {
                        metadata: {
                            label: 'CYP2C9 Inhibition',
                            description: 'Predicts inhibition of CYP2C9, relevant for drug-drug interaction risk.',
                            classificationType: ClassificationTypes.BINARY,
                            unit: 'Probability',
                            directionality: Directionality.MINIMIZE,
                            range: '0.0 - 1.0',
                            goodRange: '<0.5',
                            r2: 0.786,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    ADMET_cyp2d6_substrate_carbonmangels: {
                        metadata: {
                            label: 'CYP2D6 Substrate',
                            description: 'Predicts whether the compound is metabolized by CYP2D6 enzyme.',
                            classificationType: ClassificationTypes.BINARY,
                            unit: 'Probability',
                            directionality: Directionality.MINIMIZE,
                            range: '0.0 - 1.0',
                            goodRange: '<0.5',
                            r2: 0.718,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    ADMET_cyp2d6_veith: {
                        metadata: {
                            label: 'CYP2D6 Inhibition',
                            description: 'Predicts inhibition of CYP2D6 enzyme, which may cause drug-drug interactions.',
                            classificationType: ClassificationTypes.BINARY,
                            unit: 'Probability',
                            directionality: Directionality.MINIMIZE,
                            range: '0.0 - 1.0',
                            goodRange: '<0.5',
                            r2: 0.72,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    ADMET_cyp3a4_substrate_carbonmangels: {
                        metadata: {
                            label: 'CYP3A4 Substrate',
                            description: 'Predicts whether the compound is metabolized by CYP3A4, the most clinically relevant CYP enzyme.',
                            classificationType: ClassificationTypes.BINARY,
                            unit: 'Probability',
                            directionality: Directionality.MINIMIZE,
                            range: '0.0 - 1.0',
                            goodRange: '<0.5',
                            r2: 0.649,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    ADMET_cyp3a4_veith: {
                        metadata: {
                            label: 'CYP3A4 Inhibition',
                            description: 'Predicts inhibition of CYP3A4 activity, important for drug-drug interaction risk.',
                            classificationType: ClassificationTypes.BINARY,
                            unit: 'Probability',
                            directionality: Directionality.MINIMIZE,
                            range: '0.0 - 1.0',
                            goodRange: '<0.5',
                            r2: 0.881,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    ADMET_dili: {
                        metadata: {
                            label: 'Drug-Induced Liver Injury Risk',
                            description: 'Estimates the risk of hepatotoxicity (liver damage), a leading cause of drug withdrawal.',
                            classificationType: ClassificationTypes.BINARY,
                            unit: 'Probability',
                            directionality: Directionality.MINIMIZE,
                            range: '0.0 - 1.0',
                            goodRange: '<0.5',
                            r2: 0.883,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    ADMET_half_life_obach: {
                        metadata: {
                            label: 'Half-Life',
                            description: 'Estimates elimination half-life, an important determinant of dosing frequency.',
                            classificationType: ClassificationTypes.REGRESSION,
                            unit: 'hours',
                            directionality: Directionality.NOT_APPLICABLE,
                            range: '0.1 - 200',
                            goodRange: '12 - 48',
                            r2: 0.559,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    ADMET_herg: {
                        metadata: {
                            label: 'hERG Inhibition',
                            description: 'Predicts the likelihood of blocking the cardiac hERG potassium channel, which can cause arrhythmia.',
                            classificationType: ClassificationTypes.BINARY,
                            unit: 'Probability',
                            directionality: Directionality.MINIMIZE,
                            range: '0.0 - 1.0',
                            goodRange: '<0.5',
                            r2: 0.873,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    ADMET_hia_hou: {
                        metadata: {
                            label: 'Human Intestinal Absorption (HIA)',
                            description: 'Classifies compounds as absorbed or not absorbed in the human gastrointestinal tract.',
                            classificationType: ClassificationTypes.BINARY,
                            unit: 'Probability',
                            directionality: Directionality.MAXIMIZE,
                            range: '0.0 - 1.0',
                            goodRange: '>0.5',
                            r2: 0.983,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    ADMET_ld50_zhu: {
                        metadata: {
                            label: 'LD50',
                            description: 'Predicts the median lethal dose (LD50) in rodents, a classic measure of acute toxicity.',
                            classificationType: ClassificationTypes.REGRESSION,
                            unit: 'mg/kg',
                            directionality: Directionality.MAXIMIZE,
                            range: '2 - 20000',
                            goodRange: '>2000',
                            r2: 0.618,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    ADMET_lipophilicity_astrazeneca: {
                        metadata: {
                            label: 'Lipophilicity',
                            description: 'Predicts lipophilicity (e.g., logP/logD), which influences membrane permeability, metabolism, and distribution.',
                            classificationType: ClassificationTypes.REGRESSION,
                            unit: 'logP',
                            directionality: Directionality.NOT_APPLICABLE,
                            range: '-1 - 6',
                            goodRange: '1 - 3',
                            r2: 0.542,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    ADMET_pgp_broccatelli: {
                        metadata: {
                            label: 'P-gp Inhibition',
                            description: 'Predicts inhibition of P-glycoprotein activity. P-gp substrates can limit absorption and brain penetration.',
                            classificationType: ClassificationTypes.BINARY,
                            unit: 'Probability',
                            directionality: Directionality.MAXIMIZE,
                            range: '0.0 - 1.0',
                            goodRange: '>0.5',
                            r2: 0.932,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    ADMET_ppbr_az: {
                        metadata: {
                            label: 'Plasma Protein Binding',
                            description: 'Predicts the fraction of drug bound to plasma proteins versus free drug available for action.',
                            classificationType: ClassificationTypes.REGRESSION,
                            unit: '% bound',
                            unitType: UnitType.PERCENTAGE,
                            directionality: Directionality.MINIMIZE,
                            range: '0 - 100',
                            goodRange: '<90',
                            r2: 0.622,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    ADMET_solubility_aqsoldb: {
                        metadata: {
                            label: 'Aqueous solubility',
                            description: 'Predicts intrinsic water solubility, a key property for formulation and absorption.',
                            classificationType: ClassificationTypes.REGRESSION,
                            unit: 'g/mL',
                            directionality: Directionality.MAXIMIZE,
                            range: '4 - 4000',
                            goodRange: '>4',
                            r2: 0.787,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    ADMET_vdss_lombardo: {
                        metadata: {
                            label: 'Volume of Distribution (VDss)',
                            description: 'Predicts how extensively the drug distributes into tissues compared to plasma.',
                            classificationType: ClassificationTypes.REGRESSION,
                            unit: 'L/kg',
                            directionality: Directionality.NOT_APPLICABLE,
                            range: '<0.2 (low), 0.2 - 0.7 (mod), >0.7 (high)',
                            goodRange: '0.2 - 0.7',
                            r2: 0.716,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                },
            },
        },
    },
});

/**
 * MapLight job: computes MapLight properties and writes them onto the
 * matching workspace compounds under the `maplight` scope.
 *
 * @augments JobDefinition
 */
export class MaplightJob extends JobDefinition {
    static jobTypeId = JobTypes.MAPLIGHT;
    static resultDestination = JobResultDestinations.WORKSPACE;
    static jobTypeName = 'MapLight';
    static jobName = 'MapLight Job';

    static scopedPropertyMetadata = scopedPropertyMetadata;

    /**
     * Applies the per-structure MapLight results to the loaded compounds,
     * skipping values already present unless `stash.recalculate` is set.
     * @override
     * @param {object} _job
     * @param {object|string} results
     * @param {import('../JobDefinition').IntegrateResultsOptions} [_options]
     * @returns {Promise<import('../JobDefinition').IntegrateResultsReturn>}
     */
    async integrateResults(_job, results, _options) {
        let resultsPacket = results;
        let error;
        try {
            if (!resultsPacket) return { error: 'Empty results' };
            if (typeof resultsPacket === 'string') resultsPacket = JSON.parse(resultsPacket);
            if (!resultsPacket.jobResults) return { error: 'Empty results' };
            const { recalculate } = resultsPacket.stash || {};
            const allCmpds = App.Workspace.getLoadedCompounds();
            for (const [structId, maplightResult] of Object.entries(resultsPacket.jobResults)) {
                const compounds = allCmpds.filter((c) => c.getUniqueStructId() === structId);
                const scopeName = this.jobTypeId;
                for (const c of compounds) {
                    for (const [propName, value] of Object.entries(maplightResult)) {
                        if (recalculate || c.getProperty(scopeName, propName) === undefined) {
                            if (value !== undefined) {
                                c.setProperty(scopeName, propName, value);
                            }
                        }
                    }
                }
            }
        } catch (ex) {
            error = 'Error loading MapLight results';
            console.error(error, ex);
        }

        return { error, results: resultsPacket };
    }
}
